import { cn } from '@/shared/lib/cn';

import { Icon } from './Icon';

export interface Step {
  key: string;
  label: string;
  /** Linha de apoio abaixo do rotulo, no assistente. */
  description?: string | undefined;
  /**
   * Situacao propria da etapa no assistente: "erro" marca a etapa com campo a
   * corrigir; "concluida" marca a que ja foi preenchida, mesmo depois da atual
   * (quem volta para corrigir nao perde o que fez adiante).
   */
  state?: 'erro' | 'concluida' | undefined;
  /** Etapa que ainda nao pode ser aberta, com o motivo no titulo. */
  disabledReason?: string | undefined;
}

export interface StepperProps {
  steps: Step[];
  currentKey: string;
  /** Encerramento negativo: a progressao para onde parou. */
  terminated?: boolean | undefined;
  /**
   * Torna as etapas navegaveis, como no assistente de cotacao, endosso e cadastro de
   * corretor. Sem ele, o indicador so mostra o andamento, como na esteira.
   *
   * So abrem as etapas ja percorridas, as marcadas como concluidas e a atual: pular
   * adiante deixaria a pessoa numa etapa que depende de dado ainda nao preenchido.
   */
  onStepClick?: ((key: string) => void) | undefined;
  /** Nome do indicador para o leitor de tela, como "Etapas da cotacao". */
  label?: string | undefined;
}

/**
 * Indicador de etapas, da esteira e dos assistentes.
 *
 * Ele responde tres perguntas de uma vez: quanto ja andou, onde esta e quanto
 * falta. Uma etiqueta com o nome da etapa atual responde apenas a segunda, e e
 * justamente a distancia ate o fim que costuma ser cobrada.
 *
 * Na esteira, a ordem e o conjunto vem do servidor, e nao de uma lista mantida aqui:
 * uma etapa nova aparece sem alteracao no cliente. No assistente, as etapas sao do
 * formulario, e `useStepper` guarda em qual a pessoa esta.
 */
export function Stepper({
  steps,
  currentKey,
  terminated,
  onStepClick,
  label,
}: StepperProps) {
  const currentIndex = steps.findIndex((step) => step.key === currentKey);

  return (
    <ol
      aria-label={label}
      className="rolagem-fina flex w-full items-center gap-1 overflow-x-auto pb-1"
    >
      {steps.map((step, index) => {
        const percorrida = currentIndex >= 0 && index < currentIndex;
        const concluida =
          step.state === 'concluida' || (percorrida && step.state !== 'erro');
        const atual = index === currentIndex;
        const erro = step.state === 'erro';
        const navegavel =
          onStepClick !== undefined &&
          !step.disabledReason &&
          (percorrida || concluida || atual);

        const marcador = (
          <span
            aria-hidden
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-full',
              'text-2xs font-semibold ring-1 transition-colors',
              'duration-(--duration-base)',
              concluida && !atual && 'bg-brand-500 text-white ring-brand-500',
              atual &&
                !terminated &&
                !erro &&
                'bg-brand-50 text-brand-700 ring-brand-500',
              ((atual && terminated) || erro) &&
                'bg-danger-50 text-danger-700 ring-danger-500',
              !concluida &&
                !atual &&
                !erro &&
                'bg-surface text-ink-400 ring-line-strong',
            )}
          >
            {erro ? (
              <Icon name="alert" size={12} />
            ) : concluida && !atual ? (
              <Icon name="check" size={12} />
            ) : (
              index + 1
            )}
          </span>
        );

        const texto = (
          <>
            <span
              className={cn(
                'max-w-24 truncate text-center text-2xs',
                atual ? 'font-semibold text-ink-900' : 'text-ink-500',
                erro && 'text-danger-700',
              )}
              title={step.disabledReason ?? step.label}
            >
              {step.label}
            </span>
            {step.description ? (
              <span className="max-w-28 truncate text-center text-2xs text-ink-400">
                {step.description}
              </span>
            ) : null}
            {erro ? <span className="sr-only">com campo a corrigir</span> : null}
            {concluida && !atual ? <span className="sr-only">concluida</span> : null}
          </>
        );

        return (
          <li
            key={step.key}
            aria-current={atual ? 'step' : undefined}
            className="flex min-w-0 flex-1 items-center gap-1"
          >
            {navegavel ? (
              <button
                type="button"
                onClick={() => onStepClick(step.key)}
                className="flex min-w-0 flex-col items-center gap-1.5 rounded-md p-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
              >
                {marcador}
                {texto}
              </button>
            ) : (
              <div
                className={cn(
                  'flex min-w-0 flex-col items-center gap-1.5',
                  onStepClick && !atual && 'opacity-80',
                )}
              >
                {marcador}
                {texto}
              </div>
            )}

            {index < steps.length - 1 ? (
              <span
                aria-hidden
                className={cn(
                  'mb-5 h-px flex-1 transition-colors duration-(--duration-base)',
                  percorrida ? 'bg-brand-500' : 'bg-line-strong',
                )}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
